<script setup lang="ts">
/**
 * Compact centered confirmation dialog.
 *
 * @example
 * <AppConfirmDialog
 *   v-model:visible="open"
 *   :close-aria-label="t('order.noMapLocationDialog.close')"
 *   :confirm-label="t('order.noMapLocationDialog.confirm')"
 *   @confirm="onConfirm"
 * >
 *   <template #title>{{ t('order.noMapLocationDialog.title') }}</template>
 *   <template #body>{{ t('order.noMapLocationDialog.body') }}</template>
 * </AppConfirmDialog>
 */
const visible = defineModel<boolean>('visible', { default: false })

withDefaults(defineProps<{
  dismissableMask?: boolean
  closeAriaLabel: string
  confirmLabel?: string
}>(), {
  dismissableMask: true,
})

const emit = defineEmits<{
  confirm: []
  close: []
}>()

const slots = useSlots()
const hasActions = computed(() => !!slots.actions)

function close() {
  visible.value = false
}

function onConfirm() {
  emit('confirm')
}

function removeOrphanDialogMasks() {
  if (!import.meta.client) return
  const openDialog = [...document.querySelectorAll('[role="dialog"]')].some((el) => {
    const style = window.getComputedStyle(el)
    return style.display !== 'none' && style.visibility !== 'hidden'
  })
  if (openDialog) return
  document.querySelectorAll('.p-dialog-mask, .p-overlay-mask, [data-pc-section="mask"]').forEach((node) => {
    const el = node as HTMLElement
    if (el.querySelector('[role="dialog"]')) return
    el.remove()
  })
}

function onHide() {
  emit('close')
  nextTick(() => {
    requestAnimationFrame(removeOrphanDialogMasks)
  })
}
</script>

<template>
  <Dialog
    v-model:visible="visible"
    modal
    :dismissable-mask="dismissableMask"
    :closable="false"
    unstyled
    pt:root:class="app-dialog-root"
    @hide="onHide"
  >
    <div class="app-dialog-card">
      <button
        type="button"
        class="app-dialog-close"
        :aria-label="closeAriaLabel"
        @click="close"
      >
        <Icon name="tabler:x" size="1rem" />
      </button>

      <h2 v-if="slots.title" class="app-dialog-title">
        <slot name="title" />
      </h2>

      <p v-if="slots.body" class="app-dialog-body">
        <slot name="body" />
      </p>

      <slot />

      <div v-if="hasActions" class="app-dialog-actions">
        <slot name="actions" />
      </div>
      <button
        v-else-if="confirmLabel"
        type="button"
        class="app-dialog-confirm"
        @click="onConfirm"
      >
        {{ confirmLabel }}
      </button>
    </div>
  </Dialog>
</template>
